import { FormEvent, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, json, normalizePage, queryString } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { formatDate, StatusBadge } from "../../components/Badge";
import { Field, Select, Textarea } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Pagination,
  Success,
} from "../../components/State";

interface Operation {
  id: string;
  aggregate_type: string;
  aggregate_id: string;
  event_type: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  available_at: string;
  last_error?: string;
  dead_at?: string;
  created_at: string;
}
interface OperationPage {
  items: Operation[];
  total: number;
  page: number;
  page_size: number;
  summary: Record<string, number>;
}
export function AdminOperationsPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const status = params.get("status") || "dead";
  const result = useAsync(
    async () =>
      normalizePage(
        await api<OperationPage>(
          `/admin/operations${queryString({ page, pageSize: 20, status })}`,
        ),
      ),
    [page, status],
  );
  const [retrying, setRetrying] = useState<Operation>();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    next.set("page", "1");
    setParams(next);
  };
  const retry = async (event: FormEvent) => {
    event.preventDefault();
    if (!retrying) return;
    setBusy(true);
    setError("");
    try {
      await api(`/admin/operations/outbox/${retrying.id}/retry`, {
        method: "POST",
        body: json({ reason }),
      });
      setRetrying(undefined);
      setReason("");
      setMessage("事件已重新进入待处理队列");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "重试失败");
    } finally {
      setBusy(false);
    }
  };
  const summary = (result.data as OperationPage | undefined)?.summary || {};
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>ASYNC OPERATIONS</span>
          <h1>异步任务</h1>
          <p>死信、通知、导出、保留任务与文件扫描状态。</p>
        </div>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      <div className="metric-grid compact-metrics">
        <article>
          <span>Outbox 死信</span>
          <strong>{summary.outbox_dead || 0}</strong>
        </article>
        <article>
          <span>通知失败</span>
          <strong>{summary.notification_failed || 0}</strong>
        </article>
        <article>
          <span>导出失败</span>
          <strong>{summary.export_failed || 0}</strong>
        </article>
        <article>
          <span>保留任务异常</span>
          <strong>{summary.retention_failed || 0}</strong>
        </article>
        <article>
          <span>扫描失败</span>
          <strong>{summary.file_scan_failed || 0}</strong>
        </article>
      </div>
      <div className="admin-filterbar">
        <Select
          value={status}
          onChange={(e) => patch("status", e.target.value)}
        >
          <option value="dead">死信</option>
          <option value="failed">失败待重试</option>
          <option value="pending">待处理</option>
          <option value="in_flight">处理中</option>
          <option value="published">已发布</option>
        </Select>
      </div>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="当前没有匹配事件" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>事件</th>
                <th>聚合根</th>
                <th>尝试</th>
                <th>状态</th>
                <th>错误</th>
                <th>时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.event_type}</strong>
                    <small>{item.id}</small>
                  </td>
                  <td>
                    {item.aggregate_type}
                    <small>{item.aggregate_id}</small>
                  </td>
                  <td>
                    {item.attempt_count} / {item.max_attempts}
                  </td>
                  <td>
                    <StatusBadge status={item.status as never} />
                  </td>
                  <td>{item.last_error || "-"}</td>
                  <td>{formatDate(item.dead_at || item.created_at)}</td>
                  <td>
                    {["dead", "failed"].includes(item.status) && (
                      <button
                        onClick={() => {
                          setRetrying(item);
                          setReason("");
                        }}
                      >
                        <RefreshCw size={14} />
                        重试
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        pageSize={result.data?.page_size || 20}
        total={result.data?.total || 0}
        onChange={(value) => patch("page", String(value))}
      />
      {retrying && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            aria-label="关闭"
            onClick={() => setRetrying(undefined)}
          />
          <form className="modal-card" onSubmit={retry}>
            <header>
              <h2>重试异步事件</h2>
              <button type="button" onClick={() => setRetrying(undefined)}>
                ×
              </button>
            </header>
            <Field label="处置原因" required>
              <Textarea
                required
                minLength={5}
                maxLength={4000}
                rows={5}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <button className="primary-button" disabled={busy}>
              {busy ? "提交中..." : "重新入队"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
